import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { buildAnalysis, getAnalysisStatus } from '@/services/WorkingDataService'
import { useAuthUserStore } from '@/stores/auth'

/** Who may build analysis - mirrors insight-core MAPPING_ROLES. */
export const BUILD_ROLES = ['admin', 'curator', 'analyst']

/**
 * The current tenant's analysis: when the snapshot dashboards and Cube read was last built,
 * and whether answers, statements or rates have changed since (docs/plans/live-working-layer.md).
 * Shared by the top bar's notice and the Pipelines page.
 */
export const useAnalysisStore = defineStore('analysis', () => {
    const auth = useAuthUserStore()

    const status = ref(null)
    const building = ref(false)
    const error = ref(null)

    const canBuild = computed(() => BUILD_ROLES.includes(auth.getUserRole))

    /** Reads the status again. Without a tenant there is nothing to read. */
    async function read() {
        if (!auth.getTenantId) {
            status.value = null
            return
        }

        try {
            status.value = await getAnalysisStatus()
        } catch {
            status.value = null
        }
    }

    /** Builds the current tenant's analysis, then reads the status again. */
    async function build() {
        building.value = true
        error.value = null

        try {
            const result = await buildAnalysis()
            await read()

            return result
        } catch (e) {
            error.value = e?.response?.data?.detail ?? e?.message ?? String(e)
            throw e
        } finally {
            building.value = false
        }
    }

    return { status, building, error, canBuild, read, build }
})
